import { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Card, { CardHeader, CardTitle } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { ClipboardDocumentIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function KillSwitch() {
  const [override, setOverride] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDebug, setIsDebug] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [historyPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/auth/kill-switch/farm-override');
      setOverride(res.data.data || null);
    } catch (err) {
      setError('Failed to load kill switch state');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/auth/kill-switch/farm-override/history?page=${historyPage}&limit=10`);
      setHistory(res.data.data?.docs || []);
      setHistoryTotal(res.data.data?.total || 0);
    } catch (err) {
      console.error('Failed to load history', err);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const [activatingCookie, setActivatingCookie] = useState(false);
  const [cookieResult, setCookieResult] = useState(null);

  const handleActivateDebugCookie = async () => {
    try {
      setActivatingCookie(true);
      setCookieResult(null);
      const res = await api.get('/auth/kill-switch/farm-override/debug-cookie');
      const count = res.data.data?.cookiesSet?.length || 0;
      setCookieResult({ success: true, message: `${count} debug cookie(s) set (1 hour)` });
    } catch (err) {
      setCookieResult({
        success: false,
        message: err.response?.data?.message || 'Failed to activate debug cookie',
      });
    } finally {
      setActivatingCookie(false);
      setTimeout(() => setCookieResult(null), 5000);
    }
  };

  const openConfirmModal = () => {
    setIsDebug(false);
    setShowConfirmModal(true);
  };

  const handleToggle = async () => {
    const newEnabled = !isActive;
    setShowConfirmModal(false);

    try {
      setToggling(true);
      const res = await api.post('/auth/kill-switch/farm-override', {
        enabled: newEnabled,
        notes: notes || null,
        ...(newEnabled && isDebug ? { isDebug: true } : {}),
      });
      // Store the full response to capture debugKey/debugSecret
      if (res.data.data) {
        setOverride(res.data.data);
      }
      setNotes('');
      setShowNotes(false);
      setIsDebug(false);
      setShowSecret(false);
      await Promise.all([fetchData(), fetchHistory()]);
    } catch (err) {
      alert('Failed to update kill switch: ' + (err.response?.data?.message || err.message));
    } finally {
      setToggling(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;

  const isActive = override?.enabled === true;
  const totalPages = Math.ceil(historyTotal / 10);

  return (
    <div>
      <PageHeader
        title="Kill Switch"
        description="Emergency toggle to block customer access to quotes and payments for your farm."
      />

      {/* Current State */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Current State</CardTitle>
          <Badge variant={isActive ? 'danger' : 'success'} size="lg" dot>
            {isActive ? 'ACTIVE (Blocked)' : 'Inactive (Normal)'}
          </Badge>
        </CardHeader>

        {override && (
          <div className="text-sm text-slate-500 mb-4">
            Last changed by <span className="font-medium text-slate-700">{override.triggeredBy?.name || override.triggeredBy?.email || 'Unknown'}</span>
            {' '}on {new Date(override.createdAt).toLocaleString()}
            {override.notes && (
              <span className="block mt-1 text-slate-600 italic">"{override.notes}"</span>
            )}
          </div>
        )}

        {!override && (
          <p className="text-sm text-slate-500 mb-4">
            No farm-level kill switch override has been set. Customer access is operating normally.
          </p>
        )}

        {/* Toggle controls */}
        <div className="border-t pt-4">
          {showNotes && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Notes (optional)
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for the change..."
                maxLength={1000}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={openConfirmModal}
              disabled={toggling}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                isActive
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {toggling
                ? 'Updating...'
                : isActive
                  ? 'Deactivate Kill Switch'
                  : 'Activate Kill Switch'
              }
            </button>
            {!showNotes && (
              <button
                onClick={() => setShowNotes(true)}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                + Add notes
              </button>
            )}
          </div>
        </div>

        {/* Debug Credentials */}
        {override?.isDebug && (override?.debugKey || override?.debugSecret) && (
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Debug Credentials</h4>
            <p className="text-xs text-slate-500 mb-3">
              Set a cookie with <code className="bg-slate-100 px-1 rounded">name=debugKey</code> and <code className="bg-slate-100 px-1 rounded">value=debugSecret</code> to bypass the kill switch.
            </p>
            <div className="space-y-2">
              {override.debugKey && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500 w-16 shrink-0">Key:</span>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono flex-1 truncate">
                    {override.debugKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(override.debugKey, 'key')}
                    className="p-1 text-slate-400 hover:text-slate-600 shrink-0"
                    title="Copy key"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                  {copiedField === 'key' && <span className="text-xs text-green-600">Copied!</span>}
                </div>
              )}
              {override.debugSecret && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500 w-16 shrink-0">Secret:</span>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono flex-1 truncate">
                    {showSecret ? override.debugSecret : '••••••••••••••••'}
                  </code>
                  <button
                    onClick={() => setShowSecret(!showSecret)}
                    className="p-1 text-slate-400 hover:text-slate-600 shrink-0"
                    title={showSecret ? 'Hide secret' : 'Show secret'}
                  >
                    {showSecret ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => copyToClipboard(override.debugSecret, 'secret')}
                    className="p-1 text-slate-400 hover:text-slate-600 shrink-0"
                    title="Copy secret"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                  {copiedField === 'secret' && <span className="text-xs text-green-600">Copied!</span>}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={handleActivateDebugCookie}
                disabled={activatingCookie}
                className="px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {activatingCookie ? 'Setting cookie...' : 'Activate Debug Cookie'}
              </button>
              {cookieResult && (
                <span className={`text-xs ${cookieResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {cookieResult.message}
                </span>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* What this does */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>What does the kill switch do?</CardTitle>
        </CardHeader>
        <ul className="text-sm text-slate-600 space-y-1.5 list-disc list-inside">
          <li>Blocks all customer quote requests for your farm</li>
          <li>Blocks all customer payment and checkout flows for your farm</li>
          <li>Does NOT affect admin or internal operations</li>
          <li>Does NOT cancel jobs already in progress</li>
          <li>Can be overridden by a global admin kill switch (Layer 2)</li>
        </ul>
      </Card>

      {/* History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Change History</CardTitle>
            <span className="text-sm text-slate-500">{historyTotal} total</span>
          </CardHeader>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  <th className="pb-2 pr-4 font-medium">State</th>
                  <th className="pb-2 pr-4 font-medium">Changed By</th>
                  <th className="pb-2 pr-4 font-medium">Notes</th>
                  <th className="pb-2 pr-4 font-medium">Debug</th>
                  <th className="pb-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((entry) => (
                  <tr key={entry._id}>
                    <td className="py-2.5 pr-4">
                      <Badge variant={entry.enabled ? 'danger' : 'success'} size="sm">
                        {entry.enabled ? 'Activated' : 'Deactivated'}
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-4 text-slate-700">
                      {entry.triggeredBy?.name || entry.triggeredBy?.email || 'Unknown'}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-500 max-w-xs truncate">
                      {entry.notes || '-'}
                    </td>
                    <td className="py-2.5 pr-4">
                      {entry.isDebug && (
                        <Badge variant="warning" size="sm">Debug</Badge>
                      )}
                    </td>
                    <td className="py-2.5 text-slate-500 whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-slate-500">
                Page {historyPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  disabled={historyPage === 1}
                  className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
                  disabled={historyPage === totalPages}
                  className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {isActive ? 'Deactivate' : 'Activate'} Kill Switch
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              {isActive
                ? 'This will restore customer access to quotes and payments for your farm.'
                : 'This will block all customer access to quotes and payments for your farm.'}
            </p>

            {!isActive && (
              <label className="flex items-center gap-2 mb-4 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDebug}
                  onChange={(e) => setIsDebug(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700">Enable debug mode</span>
                  <p className="text-xs text-slate-500">Generates cookie-based bypass credentials</p>
                </div>
              </label>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleToggle}
                className={`px-4 py-2 text-sm font-medium rounded-lg text-white ${
                  isActive
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isActive ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
